"use client";

import { Check, Copy, Lock } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import {
  DATA_TABLE_ACCESS_MODE_OPTIONS,
  formatDataTableRowToolCompactLabel,
  getDataTableAllowedTools,
} from "./constants";

interface DataTableAccessModeSelectorProps {
  value: DataTableAccessMode;
  onChange: (mode: DataTableAccessMode) => void;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
  idPrefix?: string;
  showDetails?: boolean;
}

export function DataTableAccessModeSelector({
  value,
  onChange,
  disabled = false,
  compact = false,
  className,
  idPrefix = "access-mode",
  showDetails = true,
}: DataTableAccessModeSelectorProps) {
  const selectedOption =
    DATA_TABLE_ACCESS_MODE_OPTIONS.find((opt) => opt.value === value) ||
    DATA_TABLE_ACCESS_MODE_OPTIONS[0];

  const allowedTools = getDataTableAllowedTools(value);

  const handleCopyTool = (tool: DataTableRowTool) => {
    navigator.clipboard.writeText(tool);
    toast.success("Copied to clipboard");
  };

  if (compact) {
    return (
      <div className={cn("space-y-2", className)}>
        {showDetails && (
          <div>
            <p className="flex items-center gap-1.5 text-xs font-medium">
              <Lock className="size-3.5 text-muted-foreground" />
              Access
            </p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {selectedOption.description}
            </p>
          </div>
        )}

        <div
          role="radiogroup"
          aria-label="Access"
          className="inline-flex gap-1 rounded-full bg-muted p-1"
        >
          {DATA_TABLE_ACCESS_MODE_OPTIONS.map((option) => {
            const isSelected = value === option.value;
            const optionId = `${idPrefix}-${option.value}`;

            return (
              <button
                key={option.value}
                id={optionId}
                type="button"
                role="radio"
                aria-checked={isSelected}
                disabled={disabled}
                onClick={() => onChange(option.value)}
                className={cn(
                  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-semibold transition-colors px-3 py-1 text-xs",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  isSelected
                    ? "bg-background text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {showDetails && allowedTools.length > 0 && (
          <div className="flex flex-wrap gap-1" aria-label="Allowed actions">
            {allowedTools.map((tool) => (
              <span
                key={tool}
                className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-1.5 py-px text-[10px] font-medium text-muted-foreground"
              >
                <Check className="size-2.5 text-green-600" strokeWidth={3} />
                {formatDataTableRowToolCompactLabel(tool)}
                <button
                  type="button"
                  onClick={() => handleCopyTool(tool)}
                  className="transition-colors hover:text-foreground"
                  aria-label={`Copy ${tool}`}
                >
                  <Copy className="size-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Access mode"
      className={cn("grid grid-cols-1 gap-2", className)}
    >
      {DATA_TABLE_ACCESS_MODE_OPTIONS.map((option) => {
        const isSelected = value === option.value;
        const optionId = `${idPrefix}-${option.value}`;

        return (
          <button
            key={option.value}
            id={optionId}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              "w-full rounded-xl border px-3.5 py-3 text-left transition-colors",
              "disabled:cursor-not-allowed disabled:opacity-60",
              isSelected
                ? "border-primary bg-primary/[0.03] ring-1 ring-primary"
                : "border-border bg-muted/40 hover:border-primary/40 hover:bg-muted/60"
            )}
          >
            <span className="block text-sm font-medium text-foreground">
              {option.label}
            </span>
            <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
              {option.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}
